/**
 * Theme detection.
 *
 * "auto" reads the host page's own computed styles and derives a palette from
 * them, so the widget looks like it belongs rather than like a bolted-on
 * third-party box.
 *
 * Every step is defensive. Reading computed styles can fail, a host colour can
 * be a keyword or a gradient, and contrast has to be checked rather than
 * assumed - a primary colour picked off a page is frequently unreadable
 * against white. When anything is uncertain the professional default wins;
 * a widget that looks slightly generic is a far better outcome than one with
 * grey text on a grey background.
 */

import { ThemeTokens, ThemeMode } from './types';

const LIGHT_DEFAULTS: ThemeTokens = {
    primaryColor: '#2563eb',
    onPrimaryColor: '#ffffff',
    surfaceColor: '#ffffff',
    panelColor: '#f8fafc',
    textColor: '#111827',
    mutedTextColor: '#6b7280',
    borderColor: '#e5e7eb',
    borderRadius: '14px',
    fontFamily:
        'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    userBubbleColor: '#2563eb',
    userBubbleTextColor: '#ffffff',
    botBubbleColor: '#f1f5f9'
};

const DARK_DEFAULTS: ThemeTokens = {
    primaryColor: '#60a5fa',
    onPrimaryColor: '#0b1220',
    surfaceColor: '#0f172a',
    panelColor: '#111c33',
    textColor: '#e5e7eb',
    mutedTextColor: '#94a3b8',
    borderColor: '#1e293b',
    borderRadius: '14px',
    fontFamily:
        'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    userBubbleColor: '#2563eb',
    userBubbleTextColor: '#ffffff',
    botBubbleColor: '#1e293b'
};

interface Rgb {
    r: number;
    g: number;
    b: number;
}

function parseColor(value: string | null | undefined): Rgb | null {
    if (!value) {
        return null;
    }

    const rgb = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i.exec(value);
    if (rgb?.[1] !== undefined && rgb[2] !== undefined && rgb[3] !== undefined) {
        // A fully transparent colour is not a colour; treating it as black is
        // the classic source of an unreadable auto-theme.
        const alpha = /rgba\([^)]*,\s*([\d.]+)\s*\)/i.exec(value);
        if (alpha?.[1] !== undefined && Number(alpha[1]) < 0.5) {
            return null;
        }
        return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]) };
    }

    const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
    if (hex?.[1] !== undefined) {
        const raw = hex[1];
        const full =
            raw.length === 3
                ? raw
                      .split('')
                      .map((char) => char + char)
                      .join('')
                : raw;
        return {
            r: parseInt(full.slice(0, 2), 16),
            g: parseInt(full.slice(2, 4), 16),
            b: parseInt(full.slice(4, 6), 16)
        };
    }

    return null;
}

function toHex({ r, g, b }: Rgb): string {
    const part = (value: number): string =>
        Math.max(0, Math.min(255, Math.round(value)))
            .toString(16)
            .padStart(2, '0');
    return `#${part(r)}${part(g)}${part(b)}`;
}

/** WCAG relative luminance. */
function luminance({ r, g, b }: Rgb): number {
    const channel = (value: number): number => {
        const scaled = value / 255;
        return scaled <= 0.03928
            ? scaled / 12.92
            : Math.pow((scaled + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1 to 21. */
export function contrastRatio(a: Rgb, b: Rgb): number {
    const light = Math.max(luminance(a), luminance(b));
    const dark = Math.min(luminance(a), luminance(b));
    return (light + 0.05) / (dark + 0.05);
}

/** Black or white, whichever is legible on the given background. */
function readableOn(background: Rgb): string {
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 17, g: 24, b: 39 };
    return contrastRatio(background, white) >= contrastRatio(background, black)
        ? '#ffffff'
        : '#111827';
}

function isDark(color: Rgb): boolean {
    return luminance(color) < 0.4;
}

/**
 * Finds the host's accent colour.
 *
 * Tries CSS custom properties a design system would plausibly define, then
 * falls back to the computed colour of a real link or button - which is the
 * most reliable accent signal on a page that defines no variables at all.
 */
function detectPrimary(): Rgb | null {
    const root = document.documentElement;
    const rootStyles = window.getComputedStyle(root);

    const variables = [
        '--primary-color',
        '--color-primary',
        '--brand-color',
        '--color-brand',
        '--accent-color',
        '--color-accent',
        '--theme-primary',
        '--ion-color-primary',
        '--mat-primary',
        '--bs-primary'
    ];

    for (const name of variables) {
        const parsed = parseColor(rootStyles.getPropertyValue(name));
        if (parsed !== null) {
            return parsed;
        }
    }

    const candidate =
        document.querySelector('a[href]') ??
        document.querySelector('button') ??
        document.querySelector('[class*="btn"]');

    if (candidate !== null) {
        const styles = window.getComputedStyle(candidate);
        const background = parseColor(styles.backgroundColor);
        // A button's background is a better accent than its text colour, but
        // only when it is actually painted.
        if (background !== null && !isNearNeutral(background)) {
            return background;
        }
        const color = parseColor(styles.color);
        if (color !== null && !isNearNeutral(color)) {
            return color;
        }
    }

    return null;
}

/** True for greys, near-blacks and near-whites, which make poor accents. */
function isNearNeutral({ r, g, b }: Rgb): boolean {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    return max - min < 24;
}

function detectDarkMode(): boolean {
    const bodyBackground =
        parseColor(window.getComputedStyle(document.body).backgroundColor) ??
        parseColor(window.getComputedStyle(document.documentElement).backgroundColor);

    if (bodyBackground !== null) {
        return isDark(bodyBackground);
    }
    return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );
}

function detectFont(): string | null {
    try {
        const family = window.getComputedStyle(document.body).fontFamily;
        return family && family.trim() !== '' ? family : null;
    } catch {
        return null;
    }
}

function detectRadius(): string | null {
    const candidate = document.querySelector('button, [class*="btn"], input');
    if (candidate === null) {
        return null;
    }
    const radius = window.getComputedStyle(candidate).borderRadius;
    if (!radius || radius === '0px') {
        return null;
    }
    // A pill-shaped button should not make the whole panel a lozenge.
    const pixels = parseFloat(radius);
    return Number.isFinite(pixels) && pixels > 0 && pixels <= 24 ? radius : null;
}

/**
 * Produces the final token set.
 *
 * Explicit overrides beat detection, detection beats defaults, and the
 * contrast of the one colour pair the user reads most - text on the primary
 * button - is verified before it ships.
 */
export function resolveTheme(
    mode: ThemeMode,
    overrides: Partial<ThemeTokens> = {}
): ThemeTokens {
    let dark = mode === 'dark';
    if (mode === 'auto') {
        try {
            dark = detectDarkMode();
        } catch {
            dark = false;
        }
    }

    const tokens: ThemeTokens = { ...(dark ? DARK_DEFAULTS : LIGHT_DEFAULTS) };

    if (mode === 'auto') {
        try {
            const primary = detectPrimary();
            if (primary !== null) {
                tokens.primaryColor = toHex(primary);
                tokens.onPrimaryColor = readableOn(primary);
                tokens.userBubbleColor = tokens.primaryColor;
                tokens.userBubbleTextColor = tokens.onPrimaryColor;
            }

            const font = detectFont();
            if (font !== null) {
                tokens.fontFamily = font;
            }

            const radius = detectRadius();
            if (radius !== null) {
                tokens.borderRadius = radius;
            }
        } catch {
            // Detection is best-effort; the defaults are already in place.
        }
    }

    Object.assign(tokens, overrides);

    // Last line of defence. If the label on the send button would be
    // unreadable, the derived colour is discarded rather than shipped.
    const primaryRgb = parseColor(tokens.primaryColor);
    const onPrimaryRgb = parseColor(tokens.onPrimaryColor);
    if (
        primaryRgb !== null &&
        onPrimaryRgb !== null &&
        contrastRatio(primaryRgb, onPrimaryRgb) < 4.5
    ) {
        tokens.onPrimaryColor = readableOn(primaryRgb);
        tokens.userBubbleTextColor = tokens.onPrimaryColor;
    }

    return tokens;
}

export { LIGHT_DEFAULTS, DARK_DEFAULTS };
